import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Gift, Loader2, Minus, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { getProduct, money } from "@/lib/products";
import { http, formatApiError } from "@/lib/api";
import { useStock } from "@/context/StockContext";

const Line = ({ line }) => {
  const { setQty } = useCart();
  const { isSoldOut } = useStock();
  const p = getProduct(line.productId);
  if (!p) return null;
  const slug = `${p.id}-${line.shade.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div className="flex gap-4 py-5 border-b border-[#E6D7CC]" data-testid={`cart-line-${slug}`}>
      <img src={p.image} alt={p.name} className="w-20 h-24 object-cover rounded-t-full rounded-b-xl" />
      <div className="flex-1">
        <div className="flex justify-between gap-2">
          <p className="font-display text-lg">{p.name}</p>
          <p className="font-display">{money(p.price * line.qty)}</p>
        </div>
        <p className="text-sm text-[#6E504A]">{line.shade}{isSoldOut(p.id, line.shade) && <span className="ml-2 text-[#B4566A]" data-testid={`cart-soldout-${slug}`}>· Sold out — please remove</span>}</p>
        <div className="mt-3 inline-flex items-center rounded-full border border-[#E6D7CC]">
          <button className="w-9 h-9 grid place-items-center" onClick={() => setQty(p.id, line.shade, line.qty - 1)} data-testid={`cart-dec-${slug}`} aria-label="Decrease"><Minus className="w-3.5 h-3.5" /></button>
          <span className="w-6 text-center text-sm" data-testid={`cart-qty-${slug}`}>{line.qty}</span>
          <button className="w-9 h-9 grid place-items-center" onClick={() => setQty(p.id, line.shade, line.qty + 1)} data-testid={`cart-inc-${slug}`} aria-label="Increase"><Plus className="w-3.5 h-3.5" /></button>
        </div>
      </div>
    </div>
  );
};

const BundleStrip = ({ bundle, onAddOil }) => (
  <div className="mx-6 mt-4 rounded-2xl bg-[#F9ECE8] px-4 py-3 flex items-center gap-3" data-testid="cart-bundle-strip">
    <Gift className="w-5 h-5 shrink-0 text-[#B4566A]" strokeWidth={1.6} />
    <p className="text-sm flex-1" data-testid="cart-bundle-message">
      {bundle.hint ?? `Launch bundle unlocked — you're saving ${money(bundle.discount)}`}
    </p>
    {bundle.hint?.startsWith("Add a Melt") && (
      <button onClick={onAddOil} data-testid="cart-bundle-add-oil" className="shrink-0 text-xs rounded-full bg-[#281715] text-[#FAF7F2] px-3 h-8 hover:bg-[#4A2E2B] transition-colors duration-200">Add oil</button>
    )}
  </div>
);

export function CartDrawer() {
  const { lines, subtotal, bundle, total, open, setOpen, add } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const items = lines.map((l) => ({ product_id: l.productId, shade: l.shade, quantity: l.qty }));
      const { data } = await http.post("/payments/checkout", { items, origin_url: window.location.origin });
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else throw new Error("Checkout unavailable");
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 z-[60] bg-[#281715]/40 backdrop-blur-md" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} data-testid="cart-overlay" />
          <motion.aside data-lenis-prevent className="fixed right-0 top-0 bottom-0 z-[70] w-full sm:w-[440px] bg-[#FAF7F2] flex flex-col"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", stiffness: 260, damping: 32 }} data-testid="cart-drawer">
            <div className="flex items-center justify-between px-6 h-[68px] border-b border-[#E6D7CC]">
              <p className="font-display text-2xl">Your bag</p>
              <button onClick={() => setOpen(false)} data-testid="cart-close-btn" aria-label="Close"><X className="w-5 h-5" /></button>
            </div>
            {lines.length > 0 && <BundleStrip bundle={bundle} onAddOil={() => add("melt-lip-oil", "Honey Dew")} />}
            <div className="flex-1 overflow-y-auto px-6">
              {lines.length === 0 ? (
                <div className="h-full grid place-items-center text-center" data-testid="cart-empty">
                  <div>
                    <p className="font-display text-3xl">Your bag is bare.</p>
                    <p className="text-sm text-[#6E504A] mt-2">Let's fix that with a little shine.</p>
                  </div>
                </div>
              ) : lines.map((l) => <Line key={`${l.productId}-${l.shade}`} line={l} />)}
            </div>
            {lines.length > 0 && (
              <div className="p-6 border-t border-[#E6D7CC] space-y-4">
                <div className="flex justify-between"><span className="text-[#6E504A]">Subtotal</span><span data-testid="cart-subtotal">{money(subtotal)}</span></div>
                {bundle.discount > 0 && (
                  <div className="flex justify-between text-[#B4566A]" data-testid="cart-bundle-discount"><span>Launch bundle — 50% off lip oil{bundle.count > 1 ? ` ×${bundle.count}` : ""}</span><span>−{money(bundle.discount)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-[#6E504A]">Total</span><span className="font-display text-2xl" data-testid="cart-total">{money(total)}</span></div>
                <p className="text-xs text-[#6E504A]">Shipping and tax are calculated at checkout. U.S. shipping only.</p>
                <motion.button whileTap={{ scale: 0.98 }} onClick={checkout} disabled={loading} data-testid="checkout-btn"
                  className="w-full h-14 rounded-full bg-[#281715] text-[#FAF7F2] flex items-center justify-center gap-2 hover:bg-[#4A2E2B] transition-colors duration-200 disabled:opacity-80">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Secure checkout"}
                </motion.button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
